import { Banner } from '@/shared/ui/Banner';
import { useInstallOffer } from './installPrompt';

/**
 * «Установите приложение» — shown once, and only after the app has proved itself.
 *
 * The timing rule comes from the plan and is worth restating: **after a first successful action,
 * never on load.** A visitor who has just arrived has no reason to want this on their home
 * screen, and asking then is how install prompts earned their reputation. The caller decides what
 * counts — on the home screen it is «this person has a car in the garage», i.e. they have already
 * set the app up for themselves.
 *
 * Two platforms, two different pieces of UI, because the capability genuinely differs:
 *
 * - **Android/Chrome** hands over a real dialog. One button.
 * - **iOS** has no install API whatsoever. Installing means Share → «На экран „Домой"», done by
 *   hand, so all that can be offered is the instruction. This is not a lesser fallback — on iOS
 *   it is the only path that exists, and it is the path that unlocks Web Push there at all.
 */
export function InstallBanner({ enabled }: { enabled: boolean }) {
  const offer = useInstallOffer();

  if (!enabled || offer.mode === 'none') return null;

  return (
    <Banner
      tone="info"
      title="Установите приложение"
      action={
        <div className="flex gap-2">
          {offer.mode === 'native' && (
            <button
              type="button"
              onClick={() => void offer.install()}
              className="rounded-sm bg-accent px-3.5 py-2 text-[13px] font-bold text-accent-ink"
            >
              Установить
            </button>
          )}
          <button
            type="button"
            onClick={offer.dismiss}
            className="rounded-sm px-2.5 py-2 text-[13px] font-semibold text-ink-soft"
          >
            Не сейчас
          </button>
        </div>
      }
    >
      {offer.mode === 'native' ? (
        'Открывается с домашнего экрана и работает быстрее.'
      ) : (
        <>
          Нажмите <strong className="font-bold">Поделиться</strong>, затем{' '}
          <strong className="font-bold">На экран «Домой»</strong> — приложение откроется с
          домашнего экрана, без адресной строки.
        </>
      )}
    </Banner>
  );
}
